import { useTranslation } from 'react-i18next';
import { GridColDef } from '@mui/x-data-grid';
import { useState, ChangeEvent, useEffect, ChangeEventHandler } from 'react';
import { DELAY_BEFORE_SEARCH, MAX_PAGE_LIMIT } from '../../constants/numbers';
import BaranDataGrid from '../common/BaranDataGrid';
import { filterList } from '../../utils/array';
import IResponse, { IPagination } from '../../interfaces/response';
import axios from 'axios';
import { IPayment } from '../../interfaces/finance';
import { Autocomplete, Box, Card, CardContent, MenuItem, TextField, Typography } from '@mui/material';
import { exportToExcel } from '../../utils/file';
import { useDebounce } from 'use-debounce';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PaymentInfoModal from './PaymentInfoModal.tsx';
import Grid from '@mui/material/Unstable_Grid2/Grid2';
import { NUMBER_REGEX, PRICE_REGEX } from '../../constants/regex.ts';

export default function PaymentListTable({ adminId }: { adminId?: string }) {
	const [pageSize, setPageSize] = useState(MAX_PAGE_LIMIT);
	const [page, setPage] = useState(1);
	const [options, setOptions] = useState<any[]>([]);
	const [userValue, setUserValue] = useState('');
	const [searchTerm, _] = useState('');
	const [fetchedData, setFetchedData] = useState<IPayment[]>([]);
	const [pagination, setPagination] = useState<IPagination | null>();
	const [loading, setLoading] = useState(false);
	const [paymentId, setPaymentId] = useState<number | null>(null);
	const [user, setUser] = useState<any | null>(null);
	const [amount, setAmount] = useState<number | null>(null);
	const [refId, setRefId] = useState<number | null>(null);
	const [emailError, setEmailError] = useState<string>();
	const [open, setOpen] = useState(false);
	const [paymentSearch] = useDebounce(paymentId, DELAY_BEFORE_SEARCH);
	const [userSearch] = useDebounce(user?.id, DELAY_BEFORE_SEARCH);
	const [amountSearch] = useDebounce(amount, DELAY_BEFORE_SEARCH);
	const [refSearch] = useDebounce(refId, DELAY_BEFORE_SEARCH);
	const [selectedPaymentId, setSelectedPaymentId] = useState<number | null>(null);
	const headerClassNames = 'text-xs bg-background-paper';

	const { t } = useTranslation();
	const handleChangePaymentId: ChangeEventHandler<HTMLInputElement> = e => {
		const value = e.target.value;
		if (NUMBER_REGEX.test(value)) {
			setPaymentId(Number(value));
		} else {
			setPaymentId(null);
		}
	};
	const handleChangeAmount: ChangeEventHandler<HTMLInputElement> = e => {
		const value = e.target.value;
		if (PRICE_REGEX.test(value)) {
			setAmount(Number(value));
		} else {
			setAmount(null);
			setEmailError('');
		}
	};

	const handleChangeUser = (_: any, newValue: any) => {
		setUser(newValue);
	};
	const handleChangeRefId: ChangeEventHandler<HTMLInputElement> = e => {
		const value = e.target.value;
		if (NUMBER_REGEX.test(value)) {
			setRefId(Number(value));
		} else {
			setRefId(null);
		}
	};
	const handleOpen = (id: number) => {
		setSelectedPaymentId(id);
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
		setSelectedPaymentId(null);
	};
	useEffect(() => {
		(async () => {
			setLoading(true);
			axios({
				url: 'admin/users',
				params: {
					search: JSON.stringify([
						{
							field: 'name',
							value: userValue,
						},
					]),
				},
			}).then(res => {
				if (res.data.success) {
					setOptions(res.data.data);
				}
			});
			setLoading(false);

			setOptions([...options]);
		})();
	}, [userValue]);

	const handleExportExcel = () => {
		exportToExcel({ data: filteredRows, fileName: 'payment_list', t });
	};

	const properties: (keyof IPayment)[] = ['id', 'bank_ref_id'];

	const filteredRows = filterList(fetchedData, properties, searchTerm);

	const columns: GridColDef<IPayment>[] = [
		{
			field: 'id',
			headerName: t('row'),
			width: 90,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => {
				const rowIndex = filteredRows.indexOf(params.row);
				return (page - 1) * pageSize + (rowIndex + 1);
			},
		},
		{
			field: 'payment_id',
			headerName: t('payment_id'),
			flex: 1,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
		},
		{
			field: 'user',
			headerName: t('user'),
			flex: 1,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
		},
		{
			field: 'amount',
			headerName: t('amount'),
			flex: 1,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
		},
		{
			field: 'gateway',
			headerName: t('gateway'),
			flex: 1,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
		},
		{
			field: 'bank_ref_id',
			headerName: t('ref_id'),
			flex: 1,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
		},
		{
			field: 'created_at',
			headerName: t('date'),
			flex: 1,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
			renderCell: params => {
				return new Date(params.value).toLocaleDateString('fa-IR');
			},
		},
		{
			field: 'payed',
			headerName: t('status'),
			flex: 1,
			headerAlign: 'left',
			headerClassName: `${headerClassNames} ps-12`,
			align: 'left',
			renderCell: (params: any) => {
				let statusText;
				let bgColorClass;
				if (params.row.status === 1) {
					statusText = t('payed');
					bgColorClass = 'bg-success-selected text-success-main';
				} else if (params.row.status === 0 || params.row.status === -1) {
					statusText = t('payment_error');
					bgColorClass = 'bg-error-selected text-error-main';
				} else {
					statusText = t('unknown_status');
					bgColorClass = 'bg-warning-selected text-warning-main';
				}

				return <div className={`ms-10 mt-4 flex w-[max-content] items-center rounded px-3 py-0.5 text-xs font-medium ${bgColorClass}`}>{statusText}</div>;
			},
		},
		{
			field: 'action',
			headerName: t('action'),
			minWidth: 160,
			headerAlign: 'center',
			headerClassName: headerClassNames,
			align: 'center',
			renderCell: params => {
				return (
					<>
						<IconButton onClick={() => handleOpen(params.row.id)}>
							<VisibilityIcon className="text-text-secondary" />
						</IconButton>
					</>
				);
			},
		},
	];

	const getServer = () => {
		const searchJson = JSON.stringify([
			{ field: 'id', value: paymentSearch },
			{ field: 'user_id', value: userSearch },
			{ field: 'amount', value: amountSearch },
			{ field: 'bank_ref_id', value: refSearch },
		]);
		setLoading(true);
		axios<IResponse<IPayment[]>>({
			url: '/admin/payments',
			method: 'GET',
			params: {
				page: page,
				limit: pageSize,
				search: searchJson,
				user_id: adminId,
			},
		})
			.then(res => {
				if (res.data.success) {
					setPagination(res.data.pagination);
					setFetchedData(res.data.data.map(payment => ({ ...payment, payment_id: payment.id })));
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};
	useEffect(() => {
		if (page === 1) {
			getServer();
		} else {
			setPage(1);
		}
	}, [paymentSearch, userSearch, amountSearch, refSearch]);
	useEffect(() => {
		getServer();
	}, [page, pageSize]);

	return (
		<Box className="w-full">
			<Card className="my-3">
				<CardContent>
					<Typography className="mb-4 text-lg">{t('filter')}</Typography>
					<Grid container rowSpacing={2} columnSpacing={3}>
						<Grid xs={6} md={4}>
							<TextField onChange={handleChangePaymentId} fullWidth label={t('payment_id')} value={paymentId} defaultValue={''} />
						</Grid>
						<Grid xs={6} md={4}>
							<Autocomplete
								getOptionLabel={option => (typeof option === 'string' ? option : option.name)}
								filterOptions={x => x}
								options={options}
								autoComplete
								includeInputInList
								filterSelectedOptions
								value={user}
								onChange={handleChangeUser}
								noOptionsText={t('no_user')}
								onInputChange={(_, newInputValue) => {
									setUserValue(newInputValue);
								}}
								renderOption={(props, option) => {
									return (
										<MenuItem {...props} key={option.id} value={user}>
											{option.name ?? option.phone}
										</MenuItem>
									);
								}}
								renderInput={params => <TextField onChange={(e: ChangeEvent<HTMLInputElement>) => setUserValue(e.target.value)} {...params} label={`${t('search')} `} fullWidth></TextField>}
							/>
						</Grid>
						<Grid xs={6} md={4}>
							<TextField onChange={handleChangeAmount} fullWidth label={t('amount')} value={amount} helperText={emailError} defaultValue={''} />
						</Grid>
						<Grid xs={6} md={4}>
							<TextField onChange={handleChangeRefId} fullWidth label={t('ref_id')} value={refId} defaultValue={''} />
						</Grid>
					</Grid>
				</CardContent>
			</Card>
			<BaranDataGrid<IPayment> pagination={pagination} loading={loading} rows={fetchedData} columns={columns} pageSize={pageSize} setPageSize={setPageSize} page={page} setPage={setPage} onClick={fetchedData.length ? handleExportExcel : undefined} actionButtonLable={t('export_excel')} searchableFields={properties} />;
			<PaymentInfoModal open={open} title={t('see_status')} onClose={handleClose} id={selectedPaymentId || 0} />
		</Box>
	);
}
